import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { formatInr } from "../lib/accountsSettlement";
import { isBackgroundJobResponse, backgroundJobQueuedToast } from "../lib/backgroundJobs";
import { useBackgroundJobResult } from "../hooks/useBackgroundJobNotifications";
import { SectionCard, Pill } from "./Ui";
import BackendExportButton from "./BackendExportButton";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { useAuth, hasPermission } from "../context/AuthContext";
import { Loader2, RefreshCw, ShieldCheck, BookOpen } from "lucide-react";

function formatTdsInr(n) {
  return `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
}

function formatLimit(n) {
  const v = Number(n ?? 0);
  if (!v) return "—";
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function thresholdTone(status) {
  if (status === "crossed" || status === "excess_taxable") return "warning";
  if (status === "gate_off") return "default";
  return "success";
}

function fyStartIso(today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  const startYear = m >= 3 ? y : y - 1;
  return `${startYear}-04-01`;
}

/**
 * Party-wise TDS analysis with optional reassessment.
 * @param {{ partyId: string|number, compact?: boolean, showLedgerLink?: boolean, onPostedLedgers?: () => void, onViewLedger?: () => void }} props
 */
export default function PartyTdsAnalysisPanel({
  partyId,
  compact = false,
  showLedgerLink = true,
  onPostedLedgers,
  onViewLedger,
}) {
  const { user } = useAuth();
  const canManage = hasPermission(user, "tds.register.manage");
  const [loading, setLoading] = useState(false);
  const [reassessing, setReassessing] = useState(false);
  const [postingLedgers, setPostingLedgers] = useState(false);
  const [ledgerChanges, setLedgerChanges] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [applicableOnly, setApplicableOnly] = useState(false);
  const [pendingJobId, setPendingJobId] = useState("");

  const applyReassessResult = useCallback((data) => {
    if (!data) return;
    setAnalysis(data.analysis || analysis);
    const r = data.reassess || {};
    setLedgerChanges(r.ledger_changes || []);
    if (r.has_ledger_changes) {
      toast.info("TDS changed — review and post to ledgers when ready");
    }
    if ((r.errors || []).length) {
      toast.warning(`${r.errors.length} step(s) failed during reassess`);
    }
  }, [analysis]);

  const applyPostResult = useCallback((data) => {
    if (!data) return;
    setAnalysis(data.analysis || analysis);
    setLedgerChanges([]);
    const p = data.post || {};
    const preview = p.ledger_preview || {};
    const tdsCount = preview.tds_line_count ?? p.tds_vouchers_updated?.length ?? 0;
    if (tdsCount > 0) {
      toast.info(
        `${tdsCount} TDS debit line(s) on party ledger (${formatTdsInr(preview.tds_total_debit || 0)} total) — check Ledger statement tab`,
        { duration: 8000 },
      );
      onPostedLedgers?.();
    }
    if ((p.errors || []).length) {
      toast.warning(`${p.errors.length} post step(s) failed`);
    }
  }, [analysis, onPostedLedgers]);

  useBackgroundJobResult(pendingJobId, (event) => {
    setPendingJobId("");
    setReassessing(false);
    setPostingLedgers(false);
    if (event.action === "completed") {
      const result = event.result || {};
      if (result.reassess) applyReassessResult(result);
      else if (result.post) applyPostResult(result);
      load();
    }
  });

  const load = useCallback(async () => {
    if (!partyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tds/party/${partyId}/analysis`, {
        params: { fy_start: fyStartIso() },
      });
      setAnalysis(data);
    } catch (err) {
      toast.error(extractError(err));
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [partyId]);

  useEffect(() => {
    load();
  }, [load]);

  const reassess = async () => {
    if (!canManage || !partyId) return;
    setReassessing(true);
    try {
      const { data } = await api.post(`/tds/party/${partyId}/reassess`);
      if (isBackgroundJobResponse(data)) {
        const { title, message } = backgroundJobQueuedToast(data.job);
        toast.message(title, { description: message });
        setPendingJobId(data.job.job_id);
        return;
      }
      applyReassessResult(data);
      const r = data?.reassess || {};
      toast.success(
        `Reassessed — material ${r.material_lines || 0}, freight ${r.freight || 0}, labour ${r.labour || 0}`,
      );
      setReassessing(false);
    } catch (err) {
      toast.error(extractError(err));
      setReassessing(false);
    }
  };

  const postToLedgers = async () => {
    if (!canManage || !partyId) return;
    setPostingLedgers(true);
    try {
      const { data } = await api.post(`/tds/party/${partyId}/post-ledgers`);
      if (isBackgroundJobResponse(data)) {
        const { title, message } = backgroundJobQueuedToast(data.job);
        toast.message(title, { description: message });
        setPendingJobId(data.job.job_id);
        return;
      }
      applyPostResult(data);
      const p = data?.post || {};
      toast.success(
        `Posted — ${p.tds_vouchers_updated?.length || 0} TDS voucher(s), ${p.freight_gl_synced?.length || 0} freight GL sync`,
      );
      setPostingLedgers(false);
    } catch (err) {
      toast.error(extractError(err));
      setPostingLedgers(false);
    }
  };

  const rows = useMemo(() => {
    const all = analysis?.rows || [];
    return applicableOnly ? all.filter((r) => r.tds_applicable) : all;
  }, [analysis, applicableOnly]);

  const party = analysis?.party;
  const codeSummaries = analysis?.code_summaries || [];

  return (
    <div className="space-y-4" data-testid="party-tds-analysis">
      <SectionCard
        title="TDS analysis"
        subtitle={
          party
            ? `${party.name}${party.pan ? ` · PAN ${party.pan}` : ""} · FY ${analysis?.fy_start} → ${analysis?.fy_end}`
            : "Income-tax withholdings for this party (current FY)"
        }
        action={
          <div className="flex flex-wrap gap-2">
            {partyId && analysis ? (
              <BackendExportButton
                path={`/tds/party/${partyId}/analysis/export/excel`}
                params={{ fy_start: analysis.fy_start, fy_end: analysis.fy_end }}
                filename={`tds-analysis-${party?.name || partyId}.xlsx`}
                label="Download Excel"
                testId="party-tds-analysis-export"
              />
            ) : null}
            {showLedgerLink ? (
              <Button size="sm" variant="outline" asChild>
                <Link to="/accounts/tds">TDS register</Link>
              </Button>
            ) : null}
            <Button size="sm" variant="outline" onClick={load} disabled={loading || reassessing}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1">Refresh</span>
            </Button>
            {canManage ? (
              <Button size="sm" disabled={loading || reassessing} onClick={reassess}>
                {reassessing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                <span className="ml-1">Reassess party TDS</span>
              </Button>
            ) : null}
          </div>
        }
      >
        {loading && !analysis ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !analysis ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No TDS data for this party.</p>
        ) : (
          <>
            {!compact ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
                <MiniKpi label="Assessments" value={analysis.count ?? 0} />
                <MiniKpi label="With TDS" value={analysis.applicable_count ?? 0} />
                <MiniKpi label="FY payments (gross)" value={formatInr(analysis.total_gross || 0)} />
                <MiniKpi label="Total TDS deducted" value={formatTdsInr(analysis.total_tds || 0)} emphasize />
                {analysis.ledger_tds_total != null &&
                Number(analysis.ledger_tds_total) !== Number(analysis.total_tds || 0) ? (
                  <MiniKpi
                    label="On party ledger"
                    value={formatTdsInr(analysis.ledger_tds_total)}
                    emphasize
                  />
                ) : null}
              </div>
            ) : null}

            {(analysis.applicable_count ?? 0) > 0 && onViewLedger ? (
              <p className="text-xs text-muted-foreground mb-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                TDS appears on the party ledger as separate <strong>Debit (Dr)</strong> lines on the{" "}
                <button type="button" className="underline font-medium text-foreground" onClick={onViewLedger}>
                  Ledger statement
                </button>{" "}
                tab (use FY YTD or widen the date range to include purchase dates).
              </p>
            ) : null}

            {ledgerChanges.length > 0 && canManage ? (
              <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                      TDS changed — post to ledgers
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Updates TDS vouchers (party ledger) and re-syncs freight GL where already posted.
                    </p>
                  </div>
                  <Button size="sm" disabled={postingLedgers || reassessing} onClick={postToLedgers}>
                    {postingLedgers ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <BookOpen className="h-3.5 w-3.5" />
                    )}
                    <span className="ml-1">Post to ledgers</span>
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[640px]">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border/60">
                        <th className="py-1.5 pr-2 font-medium">Change</th>
                        <th className="py-1.5 pr-2 font-medium text-right">Old TDS</th>
                        <th className="py-1.5 pr-2 font-medium text-right">New TDS</th>
                        <th className="py-1.5 font-medium text-right">Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerChanges.map((c) => (
                        <tr key={`${c.kind}-${c.source_id || c.voucher_id || c.label}`} className="border-b border-border/40">
                          <td className="py-1.5 pr-2">{c.label}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums">{formatTdsInr(c.old_tds)}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums">{formatTdsInr(c.new_tds)}</td>
                          <td className="py-1.5 text-right tabular-nums font-medium">
                            {c.delta_tds > 0 ? "+" : ""}
                            {formatTdsInr(c.delta_tds)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {codeSummaries.length > 0 ? (
              <div className="mb-5 space-y-2">
                <p className="text-xs font-medium text-foreground">Payment code summary (FY)</p>
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs min-w-[920px]">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">Code</th>
                        <th className="text-left font-medium px-3 py-2">Nature</th>
                        <th className="text-right font-medium px-3 py-2">FY payments</th>
                        <th className="text-right font-medium px-3 py-2">Single limit</th>
                        <th className="text-right font-medium px-3 py-2">FY limit</th>
                        <th className="text-left font-medium px-3 py-2">Threshold</th>
                        <th className="text-right font-medium px-3 py-2">Taxable</th>
                        <th className="text-right font-medium px-3 py-2">TDS deducted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codeSummaries.map((s) => (
                        <tr key={s.nature_code || s.payment_code} className="border-t border-border/60">
                          <td className="px-3 py-2">
                            <Pill tone="info">{s.payment_code}</Pill>
                            {s.act_section ? (
                              <div className="text-[10px] text-muted-foreground mt-0.5">{s.act_section}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 max-w-[200px]">
                            <div className="font-medium">{s.label}</div>
                            <div className="text-muted-foreground">
                              {s.applicable_count}/{s.assessment_count} assessments
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">
                            {formatInr(s.fy_payment_total)}
                            {s.payment_code === "1031" && s.excess_over_threshold > 0 ? (
                              <div className="text-[10px] text-amber-700 dark:text-amber-300">
                                Excess {formatInr(s.excess_over_threshold)}
                              </div>
                            ) : s.single_max_payment > 0 && s.payment_code !== "1031" ? (
                              <div className="text-[10px] text-muted-foreground">
                                Max single {formatInr(s.single_max_payment)}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {formatLimit(s.single_threshold)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {formatLimit(s.fy_aggregate_threshold)}
                          </td>
                          <td className="px-3 py-2">
                            <Pill tone={thresholdTone(s.threshold_status)}>
                              {s.threshold_crossed ? "Crossed" : "Below"}
                            </Pill>
                            <div className="text-[10px] text-muted-foreground mt-1 max-w-[260px]">
                              {s.threshold_status_label}
                              {s.threshold_rule ? (
                                <div className="mt-1 text-[10px] leading-snug">{s.threshold_rule}</div>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {s.total_taxable > 0 ? formatInr(s.total_taxable) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">
                            {s.total_tds_deducted > 0 ? formatTdsInr(s.total_tds_deducted) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-border bg-muted/30 font-semibold">
                      <tr>
                        <td className="px-3 py-2" colSpan={2}>
                          Total
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatInr(analysis.total_gross || 0)}</td>
                        <td className="px-3 py-2" colSpan={3} />
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatInr(analysis.total_taxable || 0)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatTdsInr(analysis.total_tds || 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <label className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={applicableOnly}
                  onChange={(e) => setApplicableOnly(e.target.checked)}
                />
                Applicable only
              </label>
              <p className="text-[11px] text-muted-foreground">
                Code 1031 buyer gate:{" "}
                <Pill tone={analysis.buyer_1031_enabled ? "success" : "default"}>
                  {analysis.buyer_1031_enabled ? "ON" : "OFF"}
                </Pill>
              </p>
            </div>

            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No TDS assessments in this FY yet. Finalize vouchers or click Reassess after updating PAN.
              </p>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-xs border-collapse min-w-[900px]">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-2 font-medium">Date</th>
                      <th className="py-2 pr-2 font-medium">Code</th>
                      <th className="py-2 pr-2 font-medium">Source</th>
                      <th className="py-2 pr-2 font-medium text-right">Payment</th>
                      <th className="py-2 pr-2 font-medium text-right">FY agg.</th>
                      <th className="py-2 pr-2 font-medium text-right">Taxable</th>
                      <th className="py-2 pr-2 font-medium text-right">TDS</th>
                      {!compact ? <th className="py-2 font-medium">Reason</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-border/60 hover:bg-muted/30">
                        <td className="py-1.5 pr-2 whitespace-nowrap">{r.entry_date}</td>
                        <td className="py-1.5 pr-2">
                          <Pill tone={r.tds_applicable ? "warning" : "default"}>
                            {r.payment_code || r.section || "—"}
                          </Pill>
                          {r.catch_up ? (
                            <span className="text-[10px] text-amber-700 dark:text-amber-300 ml-1">catch-up</span>
                          ) : null}
                        </td>
                        <td className="py-1.5 pr-2">
                          <span className="font-mono">{r.source_code || "—"}</span>
                          <span className="text-muted-foreground ml-1">{r.source_kind}</span>
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{formatInr(r.gross_amount)}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums text-muted-foreground">
                          {r.fy_aggregate_with_payment > 0 ? formatInr(r.fy_aggregate_with_payment) : "—"}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">
                          {r.taxable_amount > 0 ? formatInr(r.taxable_amount) : "—"}
                        </td>
                        <td className="py-1.5 pr-2 text-right tabular-nums font-medium">
                          {r.tds_applicable ? formatTdsInr(r.tds_amount) : "—"}
                        </td>
                        {!compact ? (
                          <td className="py-1.5 text-muted-foreground max-w-[240px]">{r.reason || "—"}</td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}

function MiniKpi({ label, value, emphasize }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`text-lg font-semibold tabular-nums mt-0.5 ${emphasize ? "text-foreground" : ""}`}>{value}</p>
    </div>
  );
}
